import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { ArrowLeft, Save, ChevronDown, Target, X, Link2, Plus, Settings } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { trackWheelSaved, trackReflectionAdded, trackFocusAreasSelected } from "../utils/analytics";
import GuestAuthGate from "../components/GuestAuthGate";
import { EvoluraLogo } from "../components/EvoluraLogo";
import SEO from "../components/SEO";
import CustomizeAreasDialog from "../components/CustomizeAreasDialog";
import { fbViewContent } from "../utils/fbpixel";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";

const API = "/api";

const MIN_AREAS = 3;
const MAX_SCORE = 10;

// ── SVG Wheel Chart ──────────────────────────────────────
const WheelChart = ({ areas, onScoreChange, focusKeys = [], userPicture }) => {
  const focusSet = new Set(focusKeys);
  const size = 500;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2 - 60;
  const innerR = 55;
  const ringCount = MAX_SCORE;

  if (areas.length === 0) {
    return (
      <div className="flex justify-center">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="max-w-full h-auto" data-testid="wheel-chart-empty">
          <circle cx={cx} cy={cy} r={outerR} fill="var(--color-surface-subtle)" stroke="var(--color-border-default)" strokeWidth={1} />
          {[1, 2, 3, 4, 5].map(i => (
            <circle key={i} cx={cx} cy={cy} r={innerR + ((outerR - innerR) * i) / ringCount}
              fill="none" stroke="var(--color-border-default)" strokeWidth={0.5} opacity={0.4} />
          ))}
          <circle cx={cx} cy={cy} r={innerR} fill="var(--color-surface)" stroke="var(--color-border-default)" strokeWidth={1} />
          <text x={cx} y={cy - 8} textAnchor="middle" fontSize={13} fontWeight={600} fill="var(--color-text-placeholder)">
            Add areas
          </text>
          <text x={cx} y={cy + 10} textAnchor="middle" fontSize={11} fill="var(--color-text-placeholder)">
            to build your wheel
          </text>
        </svg>
      </div>
    );
  }

  const n = areas.length;
  const angleStep = (2 * Math.PI) / n;

  const polar = (angle, r) => ({
    x: cx + r * Math.cos(angle - Math.PI / 2),
    y: cy + r * Math.sin(angle - Math.PI / 2),
  });

  const rings = [];
  for (let i = 1; i <= ringCount; i++) {
    const r = innerR + ((outerR - innerR) * i) / ringCount;
    rings.push(
      <circle key={`ring-${i}`} cx={cx} cy={cy} r={r} fill="none"
        stroke="var(--color-border-default)" strokeWidth={0.5} opacity={0.6} />
    );
  }

  const dividers = areas.map((_, i) => {
    const angle = i * angleStep;
    const start = polar(angle, innerR);
    const end = polar(angle, outerR);
    return (
      <line key={`div-${i}`} x1={start.x} y1={start.y} x2={end.x} y2={end.y}
        stroke="var(--color-border-default)" strokeWidth={0.5} opacity={0.6} />
    );
  });

  const wedges = areas.map((area, i) => {
    if (!area.score) return null;
    const startAngle = i * angleStep;
    const endAngle = (i + 1) * angleStep;
    const r = innerR + ((outerR - innerR) * area.score) / ringCount;
    const s1 = polar(startAngle, innerR);
    const e1 = polar(startAngle, r);
    const s2 = polar(endAngle, innerR);
    const e2 = polar(endAngle, r);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

    const d = [
      `M ${s1.x} ${s1.y}`, `L ${e1.x} ${e1.y}`,
      `A ${r} ${r} 0 ${largeArc} 1 ${e2.x} ${e2.y}`,
      `L ${s2.x} ${s2.y}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${s1.x} ${s1.y}`, "Z",
    ].join(" ");

    return <path key={`wedge-${i}`} d={d} fill={area.color} opacity={focusSet.has(area.key) ? 0.7 : 0.55}
      stroke={area.color} strokeWidth={focusSet.has(area.key) ? 2.5 : 1} />;
  });

  const clickZones = areas.flatMap((area, i) => {
    const startAngle = i * angleStep;
    const endAngle = (i + 1) * angleStep;
    return Array.from({ length: ringCount }, (_, s) => {
      const score = s + 1;
      const rI = innerR + ((outerR - innerR) * s) / ringCount;
      const rO = innerR + ((outerR - innerR) * (s + 1)) / ringCount;
      const si = polar(startAngle, rI);
      const ei = polar(startAngle, rO);
      const so = polar(endAngle, rI);
      const eo = polar(endAngle, rO);
      const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
      const d = [
        `M ${si.x} ${si.y}`, `L ${ei.x} ${ei.y}`,
        `A ${rO} ${rO} 0 ${largeArc} 1 ${eo.x} ${eo.y}`,
        `L ${so.x} ${so.y}`,
        `A ${rI} ${rI} 0 ${largeArc} 0 ${si.x} ${si.y}`, "Z",
      ].join(" ");
      return (
        <path key={`click-${i}-${s}`} d={d} fill="transparent"
          className="cursor-pointer hover:fill-current" style={{ color: `${area.color}20` }}
          onClick={() => onScoreChange(area.key, score)}
          data-testid={`wheel-click-${area.key}-${score}`} />
      );
    });
  });

  const labels = areas.map((area, i) => {
    const midAngle = (i + 0.5) * angleStep;
    const labelR = outerR + 28;
    const pos = polar(midAngle, labelR);
    const degrees = ((midAngle * 180) / Math.PI) - 90;
    const flip = degrees > 90 && degrees < 270;
    const textRotation = flip ? degrees + 180 : degrees;
    return (
      <text key={`label-${i}`} x={pos.x} y={pos.y}
        textAnchor="middle" dominantBaseline="middle"
        fill={area.color} fontSize={11} fontWeight={600}
        transform={`rotate(${textRotation}, ${pos.x}, ${pos.y})`}
        data-testid={`wheel-label-${area.key}`}>
        {area.label}
      </text>
    );
  });

  const scores = areas.map((area, i) => {
    if (!area.score) return null;
    const midAngle = (i + 0.5) * angleStep;
    const scoreR = innerR + ((outerR - innerR) * area.score) / ringCount / 2 + innerR / 2;
    const pos = polar(midAngle, Math.max(scoreR, innerR + 14));
    return (
      <text key={`score-${i}`} x={pos.x} y={pos.y}
        textAnchor="middle" dominantBaseline="middle"
        fill={area.color} fontSize={14} fontWeight={700} opacity={0.9}
        data-testid={`wheel-score-${area.key}`}>
        {area.score}
      </text>
    );
  });

  return (
    <div className="flex justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="max-w-full h-auto" data-testid="wheel-chart">
        <circle cx={cx} cy={cy} r={outerR} fill="var(--color-surface-subtle)" stroke="var(--color-border-default)" strokeWidth={1} />
        {/* Profile picture in center */}
        <defs>
          <clipPath id="center-clip">
            <circle cx={cx} cy={cy} r={innerR} />
          </clipPath>
        </defs>
        {userPicture ? (
          <image href={userPicture} x={cx - innerR} y={cy - innerR} width={innerR * 2} height={innerR * 2}
            clipPath="url(#center-clip)" preserveAspectRatio="xMidYMid slice" />
        ) : (
          <circle cx={cx} cy={cy} r={innerR} fill="var(--color-surface)" stroke="var(--color-border-default)" strokeWidth={1} />
        )}
        <circle cx={cx} cy={cy} r={innerR} fill="none" stroke="white" strokeWidth={2} />
        {rings}{dividers}{wedges}{clickZones}{scores}{labels}
      </svg>
    </div>
  );
};

// ── List Input (add items to a list) ────
const ListInput = ({ items, onAdd, onRemove, placeholder, color, testIdPrefix }) => {
  const [input, setInput] = useState("");
  const handleAdd = () => {
    const text = input.trim();
    if (!text) return;
    onAdd(text);
    setInput("");
  };
  return (
    <div>
      {items.length > 0 && (
        <div className="space-y-1 mb-2">
          {items.map((item, i) => (
            <div key={i} className="flex items-start gap-2 group" data-testid={`${testIdPrefix}-item-${i}`}>
              <div className="w-1.5 h-1.5 rounded-full mt-[5px] flex-shrink-0" style={{ background: color }} />
              <span className="text-xs flex-1 leading-relaxed" style={{ color: "var(--color-text-primary)" }}>{item}</span>
              <button onClick={() => onRemove(i)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-red-50"
                data-testid={`${testIdPrefix}-remove-${i}`}>
                <X className="w-3 h-3 text-slate-400 hover:text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-1.5">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder={placeholder}
          className="flex-1 text-xs rounded-lg px-3 py-2 focus:outline-none transition-all"
          style={{
            background: "var(--color-surface)",
            color: "var(--color-text-primary)",
            border: "1px solid var(--color-border-default)",
          }}
          data-testid={`${testIdPrefix}-input`}
        />
        <button onClick={handleAdd} disabled={!input.trim()}
          className="text-[10px] font-semibold px-2.5 py-1.5 rounded-lg transition-all disabled:opacity-30"
          style={{ background: `${color}15`, color: color, border: `1px solid ${color}30` }}
          data-testid={`${testIdPrefix}-add-btn`}>
          Add
        </button>
      </div>
    </div>
  );
};

// ── Area Row (toggle + score + auto-reveal reflection) ────
const AreaRow = ({ area, onScoreChange, reflection, onReflectionChange, isExpanded, onFocusArea, isFocused, linkedItems, onLinkFromSwot, onUnlink, swotItems, guest }) => {
  const whyList = reflection?.why_score || [];
  const improveList = reflection?.what_to_improve || [];
  const actionList = reflection?.action_steps || [];
  const hasReflection = whyList.length > 0 || improveList.length > 0 || actionList.length > 0;
  const [showSwotPicker, setShowSwotPicker] = useState(null); // "why_score" | "what_to_improve" | "action_steps" | null

  // Group linked items by reflection field
  const linkedByField = {
    why_score: (linkedItems || []).filter(i => i.reflection_field === "why_score"),
    what_to_improve: (linkedItems || []).filter(i => i.reflection_field === "what_to_improve"),
    action_steps: (linkedItems || []).filter(i => i.reflection_field === "action_steps"),
  };

  // Available SWOT items to pick from for this area+field.
  // Includes:
  //   • untagged items (linked_wheel_area absent) — usable anywhere, like before
  //   • items already tagged to THIS area but not yet placed under a reflection
  //     field — those should surface here so the user can pick where they belong
  // Excludes:
  //   • items tagged to a different area
  //   • items already placed under a reflection field (they render as badges)
  const getAvailableSwot = (field) => {
    const catMap = { why_score: "strengths", what_to_improve: "weaknesses", action_steps: "opportunities" };
    const category = catMap[field];
    if (!swotItems || !category) return [];
    return (swotItems[category] || []).filter(i => {
      if (i.deleted) return false;
      const tag = i.linked_wheel_area;
      const placed = i.linked_reflection_field;
      if (!tag) return true; // untagged → always pickable
      if (tag !== area.key) return false; // tagged to a different area
      return !placed; // tagged to this area, not yet placed under a reflection field
    });
  };

  const LinkedItemBadge = ({ item }) => (
    <div className="flex items-start gap-2 group/linked" data-testid={`linked-swot-${item.item_id}`}>
      <Link2 className="w-3 h-3 mt-[3px] text-violet-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-xs leading-relaxed text-slate-700">{item.content}</span>
        {item.suggested_by_name && (
          <div className="flex items-center gap-1 mt-0.5">
            <Avatar className="h-3.5 w-3.5">
              {item.suggested_by_picture && <AvatarImage src={item.suggested_by_picture} alt={item.suggested_by_name} />}
              <AvatarFallback className="bg-violet-100 text-violet-600 text-[6px]">
                {item.suggested_by_name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <span className="text-[9px] text-violet-500">Suggested by {item.suggested_by_name}</span>
          </div>
        )}
      </div>
      {!guest && (
        <button
          onClick={() => onUnlink(item.category, item.item_id)}
          className="opacity-0 group-hover/linked:opacity-100 p-0.5 rounded hover:bg-red-50 transition-opacity flex-shrink-0"
          data-testid={`unlink-wheel-${item.item_id}`}
        >
          <X className="w-3 h-3 text-slate-400 hover:text-red-500" />
        </button>
      )}
    </div>
  );

  const SwotPickerSection = ({ field, label }) => {
    const available = getAvailableSwot(field);
    if (available.length === 0) return null;
    if (showSwotPicker !== field) return (
      <button
        onClick={() => setShowSwotPicker(field)}
        className="flex items-center gap-1 text-[10px] text-violet-500 hover:text-violet-700 font-medium mt-1 transition-colors"
        data-testid={`link-from-swot-${area.key}-${field}`}
      >
        <Plus className="w-3 h-3" /> Link from SWOT
      </button>
    );
    return (
      <div className="mt-1.5 p-2 rounded-lg bg-violet-50/50 border border-violet-100" data-testid={`swot-picker-${area.key}-${field}`}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-medium text-violet-600">{label}</span>
          <button onClick={() => setShowSwotPicker(null)} className="p-0.5">
            <X className="w-3 h-3 text-slate-400" />
          </button>
        </div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {available.map(item => {
            const preTagged = item.linked_wheel_area === area.key;
            return (
              <button
                key={item.item_id}
                onClick={() => { onLinkFromSwot(item.category || field === "why_score" ? "strengths" : field === "what_to_improve" ? "weaknesses" : "opportunities", item.item_id, area.key); setShowSwotPicker(null); }}
                className="w-full text-left text-xs text-slate-700 px-2 py-1.5 rounded-md hover:bg-violet-100 transition-colors flex items-start gap-2"
                data-testid={`pick-swot-${item.item_id}`}
              >
                <Link2 className="w-3 h-3 text-violet-400 flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed flex-1">{item.content}</span>
                {preTagged && (
                  <span
                    className="flex-shrink-0 text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600 font-medium"
                    title="You tagged this to this area in SWOT"
                  >
                    from SWOT
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div data-testid={`area-row-${area.key}`}>
      <div className="flex items-center gap-2.5 pt-2.5 pb-1">
        {/* Color dot + Label */}
        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: area.color }} />
        <button className="text-sm font-medium flex-1 min-w-0 text-left flex items-center gap-1.5"
          style={{ color: "var(--color-text-primary)" }}
          onClick={() => area.score && onFocusArea(isExpanded ? null : area.key)}
          disabled={!area.score}>
          <span className="truncate">{area.label}</span>
          {isFocused && (
            <Target className="w-4 h-4 flex-shrink-0" style={{ color: area.color }}
              data-testid={`focus-tag-${area.key}`} />
          )}
          {hasReflection && !isExpanded && !isFocused && (
            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: area.color }} />
          )}
          {area.score && (
            <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
              style={{ color: hasReflection ? area.color : "var(--color-text-placeholder)" }} />
          )}
        </button>
      </div>
      {area.description && (
        <p className="text-[11px] ml-[22px] mb-1" style={{ color: "var(--color-text-placeholder)" }}>
          {area.description}
        </p>
      )}
      <div className="flex items-center gap-2.5 pb-2.5 ml-[22px]">

        {/* Score buttons */}
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(s => (
            <button key={s}
              onClick={() => onScoreChange(area.key, s)}
              className="w-6 h-7 rounded-md text-[10px] font-semibold transition-all"
              style={{
                background: area.score === s ? area.color : "var(--color-surface-subtle)",
                color: area.score === s ? "#fff" : "var(--color-text-secondary)",
                border: area.score === s ? "none" : "1px solid var(--color-border-default)",
              }}
              data-testid={`score-btn-${area.key}-${s}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Auto-revealed reflection panel */}
      {isExpanded && area.score && (
        <div className="ml-6 mb-2 rounded-xl p-3.5 space-y-3"
          style={{ background: `${area.color}06`, border: `1px solid ${area.color}20` }}
          data-testid={`reflection-panel-${area.key}`}>
          <div>
            <label className="text-[11px] font-semibold mb-1.5 block" style={{ color: area.color }}>
              What's going well in this area?
            </label>
            {linkedByField.why_score.length > 0 && (
              <div className="mb-1.5 space-y-1">
                {linkedByField.why_score.map(li => <LinkedItemBadge key={li.item_id} item={li} />)}
              </div>
            )}
            <ListInput
              items={whyList}
              onAdd={(text) => { onReflectionChange(area.key, "why_score", [...whyList, text]); trackReflectionAdded({ areaKey: area.key, field: "why_score", source: "wheel" }); }}
              onRemove={(i) => onReflectionChange(area.key, "why_score", whyList.filter((_, idx) => idx !== i))}
              placeholder="Add a reason..."
              color={area.color}
              testIdPrefix={`why-score-${area.key}`}
            />
            {!guest && <SwotPickerSection field="why_score" label="Pick a Strength to link" />}
          </div>
          <div>
            <label className="text-[11px] font-semibold mb-1.5 block" style={{ color: area.color }}>
              What would make it a 10?
            </label>
            {linkedByField.what_to_improve.length > 0 && (
              <div className="mb-1.5 space-y-1">
                {linkedByField.what_to_improve.map(li => <LinkedItemBadge key={li.item_id} item={li} />)}
              </div>
            )}
            <ListInput
              items={improveList}
              onAdd={(text) => { onReflectionChange(area.key, "what_to_improve", [...improveList, text]); trackReflectionAdded({ areaKey: area.key, field: "what_to_improve", source: "wheel" }); }}
              onRemove={(i) => onReflectionChange(area.key, "what_to_improve", improveList.filter((_, idx) => idx !== i))}
              placeholder="Add something to improve..."
              color={area.color}
              testIdPrefix={`what-to-improve-${area.key}`}
            />
            {!guest && <SwotPickerSection field="what_to_improve" label="Pick a Weakness to link" />}
          </div>
          <div>
            <label className="text-[11px] font-semibold mb-1.5 block" style={{ color: area.color }}>
              What can you do now to progress in this area?
            </label>
            {linkedByField.action_steps.length > 0 && (
              <div className="mb-1.5 space-y-1">
                {linkedByField.action_steps.map(li => <LinkedItemBadge key={li.item_id} item={li} />)}
              </div>
            )}
            <ListInput
              items={actionList}
              onAdd={(text) => { onReflectionChange(area.key, "action_steps", [...actionList, text]); trackReflectionAdded({ areaKey: area.key, field: "action_steps", source: "wheel" }); }}
              onRemove={(i) => onReflectionChange(area.key, "action_steps", actionList.filter((_, idx) => idx !== i))}
              placeholder="Add an action step..."
              color={area.color}
              testIdPrefix={`action-steps-${area.key}`}
            />
            {!guest && <SwotPickerSection field="action_steps" label="Pick an Opportunity to link" />}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Balance Meter ─────────────────────────────────────────
const BalanceMeter = ({ areas }) => {
  const scored = areas.filter(a => a.score);
  if (scored.length < 3) return null;
  const avg = scored.reduce((s, a) => s + a.score, 0) / scored.length;

  return (
    <div className="mt-4 text-center" data-testid="balance-meter">
      <span className="text-xs font-semibold" style={{ color: "var(--color-text-secondary)" }}>
        Average: <span style={{ color: "var(--color-brand-primary)" }}>{avg.toFixed(1)}</span>/10
      </span>
    </div>
  );
};

// ── Focus Selection Popup ─────────────────────────────────
const FocusPopup = ({ areas, selectedFocus, onConfirm, onClose, guest, onGuestSave }) => {
  const [picked, setPicked] = useState(new Set(selectedFocus || []));

  // Sort by lowest score first
  const sorted = [...areas].sort((a, b) => (a.score || 99) - (b.score || 99));

  const toggle = (key) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else if (next.size < 3) {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="focus-popup">
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative rounded-2xl p-6 max-w-md mx-4 shadow-xl w-full"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border-default)" }}>
        <div className="text-center mb-5">
          <div className="w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: "var(--color-brand-primary)10" }}>
            <Target className="w-5 h-5" style={{ color: "var(--color-brand-primary)" }} />
          </div>
          <h3 className="text-lg font-bold mb-1" style={{ color: "var(--color-text-primary)" }}>
            What matters most right now?
          </h3>
          <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
            Choose 3 areas you want to focus on improving
          </p>
        </div>

        <div className="space-y-2 mb-5 max-h-64 overflow-y-auto">
          {sorted.map(area => {
            const isFocused = picked.has(area.key);
            const isDisabled = !isFocused && picked.size >= 3;
            return (
              <button key={area.key}
                onClick={() => toggle(area.key)}
                disabled={isDisabled}
                className="w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left"
                style={{
                  background: isFocused ? `${area.color}12` : "var(--color-surface-subtle)",
                  border: isFocused ? `2px solid ${area.color}` : "1.5px solid var(--color-border-default)",
                  opacity: isDisabled ? 0.4 : 1,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                }}
                data-testid={`focus-pick-${area.key}`}>
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: area.color }} />
                <span className="text-sm font-medium flex-1" style={{ color: isFocused ? area.color : "var(--color-text-primary)" }}>
                  {area.label}
                </span>
                <span className="text-xs font-semibold" style={{ color: "var(--color-text-placeholder)" }}>
                  {area.score || "–"}/10
                </span>
                {isFocused && (
                  <Target className="w-4 h-4 flex-shrink-0" style={{ color: area.color }} />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold" style={{
            color: picked.size === 3 ? "var(--color-strengths-text)" : "var(--color-text-placeholder)"
          }}>
            {picked.size} of 3 selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline"
              onClick={onClose}
              className="rounded-full h-9 px-4 text-sm"
              data-testid="focus-cancel-btn">
              Cancel
            </Button>
            {guest ? (
              <Button
                onClick={() => onGuestSave([...picked])}
                disabled={picked.size !== 3}
                className="rounded-full h-9 px-5 text-sm font-semibold"
                style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
                data-testid="focus-save-signin-btn">
                Save & Sign in
              </Button>
            ) : (
              <Button
                onClick={() => onConfirm([...picked])}
                disabled={picked.size !== 3}
                className="rounded-full h-9 px-5 text-sm font-semibold"
                style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
                data-testid="focus-confirm-btn">
                Confirm Focus
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────
const GUEST_STORAGE_KEY = "evoluras_guest_wheel";

// Default areas fallback (used when API unavailable in guest mode)
const DEFAULT_AREAS = [
  { key: "health_fitness", label: "Health", color: "#22c55e", description: "Physical well-being and fitness habits" },
  { key: "intellect", label: "Intellect", color: "#3b82f6", description: "Learning, curiosity, and mental growth" },
  { key: "emotions", label: "Emotions", color: "#ec4899", description: "Emotional awareness and regulation" },
  { key: "hobbies", label: "Hobbies", color: "#8b5cf6", description: "Activities you enjoy and your creative outlets" },
  { key: "spirituality", label: "Spirituality", color: "#14b8a6", description: "Connection to purpose, meaning, or faith" },
  { key: "relationships", label: "Love Relationships", color: "#f43f5e", description: "Romantic partnerships and deep connections" },
  { key: "family", label: "Family", color: "#f97316", description: "Your role and relationships within your family" },
  { key: "social_life", label: "Social Life", color: "#eab308", description: "Friendships, community, and social connections" },
  { key: "finances", label: "Finances", color: "#10b981", description: "Financial health and security" },
  { key: "career", label: "Career", color: "#6366f1", description: "Professional pursuits and ambitions" },
];

const WheelOfLifePage = ({ guest = false }) => {
  const auth = useAuth();
  const user = guest ? null : auth?.user;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allAreas, setAllAreas] = useState([]);         // master list from backend
  const [activeKeys, setActiveKeys] = useState(new Set()); // which areas are toggled on
  const [scores, setScores] = useState({});              // { key: score }
  const [reflections, setReflections] = useState({});    // { key: { why_score, what_to_improve } }
  const [hasExisting, setHasExisting] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);  // which area's reflection is open
  const [focusKeys, setFocusKeys] = useState([]);        // 3 focus area keys
  const [showFocusPopup, setShowFocusPopup] = useState(false);
  const [focusPromptShown, setFocusPromptShown] = useState(false); // prevent re-triggering
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [swotData, setSwotData] = useState(null); // user's SWOT items for linking
  const [linkedItemsMap, setLinkedItemsMap] = useState({}); // { areaKey: [...items] }
  const [showCustomizeDialog, setShowCustomizeDialog] = useState(false);
  const [defaultAreas, setDefaultAreas] = useState([]);       // 10 Standard Wheel
  const [suggestedAreas, setSuggestedAreas] = useState([]);   // 10 suggestions
  const [maxAreas, setMaxAreas] = useState(12);
  const pendingNavRef = useRef(null);
  const savedStateRef = useRef(null);

  // Compute whether there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    const saved = savedStateRef.current;
    if (!saved) return activeKeys.size > 0;
    const savedKeys = saved.keys;
    const savedScores = saved.scores;
    const savedReflections = saved.reflections || {};
    const savedFocus = saved.focusKeys || [];
    if (activeKeys.size !== savedKeys.size) return true;
    for (const k of activeKeys) {
      if (!savedKeys.has(k)) return true;
      if ((scores[k] || null) !== (savedScores[k] || null)) return true;
      const curr = reflections[k] || {};
      const prev = savedReflections[k] || {};
      if (JSON.stringify(curr.why_score || []) !== JSON.stringify(prev.why_score || [])) return true;
      if (JSON.stringify(curr.what_to_improve || []) !== JSON.stringify(prev.what_to_improve || [])) return true;
      if (JSON.stringify(curr.action_steps || []) !== JSON.stringify(prev.action_steps || [])) return true;
    }
    for (const k of savedKeys) {
      if (!activeKeys.has(k)) return true;
    }
    if (JSON.stringify(focusKeys) !== JSON.stringify(savedFocus)) return true;
    return false;
  }, [activeKeys, scores, reflections, focusKeys]);

  // Block in-app navigation when unsaved — intercept all pushState calls
  const isDirtyRef = useRef(false);
  useEffect(() => { isDirtyRef.current = hasUnsavedChanges(); }, [hasUnsavedChanges, activeKeys, scores, reflections, focusKeys]);

  useEffect(() => {
    const origPushState = window.history.pushState.bind(window.history);
    const origReplaceState = window.history.replaceState.bind(window.history);

    const wheelPath = guest ? "/wheel" : "/tools/wheel";

    window.history.pushState = function (state, title, url) {
      if (isDirtyRef.current && url && !url.includes(wheelPath)) {
        pendingNavRef.current = url;
        setShowLeaveDialog(true);
        return; // block the navigation
      }
      return origPushState(state, title, url);
    };

    // Handle browser back button
    const handlePopState = () => {
      if (isDirtyRef.current) {
        // Push current state back to stay on page
        origPushState(null, "", wheelPath);
        pendingNavRef.current = -1; // signal to go back
        setShowLeaveDialog(true);
      }
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.history.pushState = origPushState;
      window.history.replaceState = origReplaceState;
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const confirmLeave = (doSave) => async () => {
    if (doSave && canSave) await saveWheel();
    setShowLeaveDialog(false);
    isDirtyRef.current = false; // allow navigation through pushState
    const dest = pendingNavRef.current;
    pendingNavRef.current = null;
    if (dest === -1) {
      window.history.back();
    } else if (dest) {
      navigate(dest);
    }
  };

  // Block browser tab close / refresh (skip when auth gate is open — user is intentionally leaving)
  useEffect(() => {
    const handler = (e) => {
      if (showAuthGate) return;
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges, showAuthGate]);

  // Helper to save guest state to localStorage
  const saveGuestState = useCallback(() => {
    const data = {
      scores,
      reflections,
      focusKeys,
      activeKeys: [...activeKeys],
    };
    localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(data));
  }, [scores, reflections, focusKeys, activeKeys]);

  // Auto-save guest state on changes
  useEffect(() => {
    if (guest && allAreas.length > 0) {
      saveGuestState();
    }
  }, [guest, scores, reflections, focusKeys, activeKeys, allAreas, saveGuestState]);

  const fetchWheel = useCallback(async () => {
    if (guest) {
      // Guest mode: try API first, fall back to hardcoded defaults
      let areas = DEFAULT_AREAS;
      let suggested = [];
      let max = 12;
      try {
        const res = await fetch(`${API}/tools/wheel/areas`);
        if (res.ok) {
          const data = await res.json();
          if (data.default_areas?.length > 0) {
            areas = data.default_areas;
          } else if (data.available_areas?.length > 0) {
            areas = data.available_areas;
          }
          suggested = data.suggested_areas || [];
          max = data.max_areas || 12;
        }
      } catch { /* use default areas */ }

      setDefaultAreas(areas);
      setSuggestedAreas(suggested);
      setMaxAreas(max);
      setAllAreas(areas);
      setActiveKeys(new Set(areas.map(a => a.key)));

      // Load saved guest state from localStorage
      const saved = localStorage.getItem(GUEST_STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.scores) setScores(parsed.scores);
          if (parsed.reflections) setReflections(parsed.reflections);
          if (parsed.focusKeys && parsed.focusKeys.length > 0) {
            setFocusKeys(parsed.focusKeys);
            setFocusPromptShown(true);
          }
          setHasExisting(true);
          savedStateRef.current = {
            keys: new Set(areas.map(a => a.key)),
            scores: { ...parsed.scores },
            reflections: JSON.parse(JSON.stringify(parsed.reflections || {})),
            focusKeys: parsed.focusKeys || [],
          };
        } catch { /* ignore parse errors */ }
      } else {
        savedStateRef.current = { keys: new Set(areas.map(a => a.key)), scores: {}, reflections: {}, focusKeys: [] };
      }

      setLoading(false);
      return;
    }

    // Authenticated mode
    try {
      const res = await fetch(`${API}/tools/wheel`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const defaults = data.default_areas || data.available_areas || [];
        const suggested = data.suggested_areas || [];
        const existing = data.wheel?.areas || [];
        setDefaultAreas(defaults);
        setSuggestedAreas(suggested);
        setMaxAreas(data.max_areas || 12);
        // Merge: defaults + suggested + any custom areas the user has saved
        const existingKeys = new Set(existing.map(a => a.key));
        const registeredKeys = new Set([...defaults, ...suggested].map(a => a.key));
        const customs = existing.filter(a => !registeredKeys.has(a.key));
        const mergedAll = [...defaults, ...suggested, ...customs];
        setAllAreas(mergedAll);

        if (data.wheel) {
          // Active = what the user has saved
          setActiveKeys(existingKeys);
          setHasExisting(true);
          const scoreMap = {};
          const reflMap = {};
          for (const a of existing) {
            if (a.score) scoreMap[a.key] = a.score;
            if (a.why_score || a.what_to_improve || a.action_steps) {
              const ws = a.why_score || "";
              const wi = a.what_to_improve || "";
              const ac = a.action_steps || "";
              reflMap[a.key] = {
                why_score: Array.isArray(ws) ? ws : (ws ? [ws] : []),
                what_to_improve: Array.isArray(wi) ? wi : (wi ? [wi] : []),
                action_steps: Array.isArray(ac) ? ac : (ac ? [ac] : []),
              };
            }
          }
          setScores(scoreMap);
          setReflections(reflMap);
          if (data.wheel.focus_areas) {
            setFocusKeys(data.wheel.focus_areas);
            setFocusPromptShown(true);
          }
          savedStateRef.current = { keys: new Set(existingKeys), scores: { ...scoreMap }, reflections: JSON.parse(JSON.stringify(reflMap)), focusKeys: data.wheel.focus_areas || [] };
        } else {
          // New user — start with the default 10 areas as the Standard Wheel
          const defaultKeys = new Set(defaults.map(a => a.key));
          setActiveKeys(defaultKeys);
          savedStateRef.current = { keys: new Set(defaultKeys), scores: {}, reflections: {}, focusKeys: [] };
        }
      }
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWheel(); }, [fetchWheel]);

  // Fetch SWOT data for linking (authenticated users only)
  useEffect(() => {
    if (guest) return;
    const fetchSwot = async () => {
      try {
        const res = await fetch(`${API}/tools/swot`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          // Normalize and build linked items map
          const linkedMap = {};
          for (const cat of ["strengths", "weaknesses", "opportunities"]) {
            for (const item of (data[cat] || [])) {
              const normalized = { ...item, item_id: item.item_id || item.id, content: item.content || item.text || "", category: cat };
              if (normalized.linked_wheel_area && !normalized.deleted) {
                const areaKey = normalized.linked_wheel_area;
                if (!linkedMap[areaKey]) linkedMap[areaKey] = [];
                linkedMap[areaKey].push({
                  item_id: normalized.item_id,
                  content: normalized.content,
                  category: cat,
                  reflection_field: normalized.linked_reflection_field || "",
                  suggested_by_name: normalized.suggested_by_name,
                  suggested_by_picture: normalized.suggested_by_picture,
                });
              }
            }
          }
          setLinkedItemsMap(linkedMap);
          setSwotData({
            strengths: (data.strengths || []).map(i => ({ ...i, item_id: i.item_id || i.id, content: i.content || i.text || "", category: "strengths" })),
            weaknesses: (data.weaknesses || []).map(i => ({ ...i, item_id: i.item_id || i.id, content: i.content || i.text || "", category: "weaknesses" })),
            opportunities: (data.opportunities || []).map(i => ({ ...i, item_id: i.item_id || i.id, content: i.content || i.text || "", category: "opportunities" })),
          });
        }
      } catch { /* ignore */ }
    };
    fetchSwot();
  }, [guest]);

  const handleLinkFromSwot = async (category, itemId, wheelArea) => {
    try {
      const res = await fetch(`${API}/tools/swot/item/${category}/${itemId}/link`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ wheel_area: wheelArea }),
      });
      if (res.ok) {
        const item = swotData?.[category]?.find(i => i.item_id === itemId);
        if (item) {
          const fieldMap = { strengths: "why_score", weaknesses: "what_to_improve", opportunities: "action_steps" };
          const newLinked = {
            item_id: item.item_id,
            content: item.content,
            category,
            reflection_field: fieldMap[category],
            suggested_by_name: item.suggested_by_name,
            suggested_by_picture: item.suggested_by_picture,
          };
          setLinkedItemsMap(prev => ({
            ...prev,
            [wheelArea]: [...(prev[wheelArea] || []), newLinked]
          }));
          // Mark as linked in swotData
          setSwotData(prev => ({
            ...prev,
            [category]: prev[category].map(i => i.item_id === itemId ? { ...i, linked_wheel_area: wheelArea } : i)
          }));
          toast.success("Linked from SWOT");
        }
      }
    } catch {
      toast.error("Failed to link");
    }
  };

  const handleUnlinkFromWheel = async (category, itemId) => {
    try {
      const res = await fetch(`${API}/tools/swot/item/${category}/${itemId}/unlink`, {
        method: "PATCH",
        credentials: "include",
      });
      if (res.ok) {
        setLinkedItemsMap(prev => {
          const updated = {};
          for (const [key, items] of Object.entries(prev)) {
            updated[key] = items.filter(i => i.item_id !== itemId);
          }
          return updated;
        });
        setSwotData(prev => ({
          ...prev,
          [category]: (prev[category] || []).map(i => i.item_id === itemId ? { ...i, linked_wheel_area: undefined } : i)
        }));
        toast.success("Unlinked");
      }
    } catch {
      toast.error("Failed to unlink");
    }
  };

  // FB Pixel: track tool view for guests
  useEffect(() => {
    if (guest) fbViewContent("Wheel of Life", "self_assessment_tool");
  }, [guest]);

  const handleScoreChange = (key, score) => {
    const newScores = { ...scores, [key]: scores[key] === score ? undefined : score };
    setScores(newScores);
    // Auto-expand reflection for the scored area
    if (scores[key] !== score) {
      setExpandedKey(key);
    } else {
      setExpandedKey(null);
    }

    // Check if all areas are now scored — trigger focus popup once
    if (!focusPromptShown && focusKeys.length === 0 && scores[key] !== score) {
      const allNowScored = allAreas.every(a => a.key === key ? true : !!scores[a.key]);
      if (allNowScored && allAreas.length > 0) {
        setTimeout(() => setShowFocusPopup(true), 400);
        setFocusPromptShown(true);
      }
    }
  };

  const handleReflectionChange = (key, field, value) => {
    setReflections(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const handleCustomizeApply = ({ activeKeys: nextKeys, customs }) => {
    // Merge master list = defaults + suggested + applied customs (client-side keys).
    // Custom keys without `custom_` prefix in the final list → backend will assign on save.
    const mergedAll = [...defaultAreas, ...suggestedAreas, ...customs];
    // Dedupe by key (prefer whatever exists first)
    const seen = new Set();
    const deduped = [];
    for (const a of mergedAll) {
      if (seen.has(a.key)) continue;
      seen.add(a.key);
      deduped.push(a);
    }
    setAllAreas(deduped);
    setActiveKeys(new Set(nextKeys));
    // Prune focus keys that are no longer active
    setFocusKeys((prev) => prev.filter((k) => nextKeys.has(k)));
  };

  // Build wheel areas from active keys (preserving order from master list)
  const wheelAreas = allAreas
    .filter(a => activeKeys.has(a.key))
    .map(a => ({ ...a, score: scores[a.key] || null }));

  const activeScored = wheelAreas.filter(a => a.score);
  const allScored = wheelAreas.length >= 1 && wheelAreas.every(a => a.score);
  const canSave = wheelAreas.length >= 1;
  const scoredCount = activeScored.length;

  const saveWheel = async () => {
    if (wheelAreas.length < 1) {
      toast.error("Add at least 1 area");
      return;
    }

    // Guest mode: show auth gate instead of saving to API
    if (guest) {
      saveGuestState();
      setShowAuthGate(true);
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/tools/wheel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          areas: wheelAreas.map(a => ({
            key: a.key,
            label: a.label,
            color: a.color,
            description: a.description,
            score: a.score,
            why_score: reflections[a.key]?.why_score || [],
            what_to_improve: reflections[a.key]?.what_to_improve || [],
            action_steps: reflections[a.key]?.action_steps || [],
          })),
          focus_areas: focusKeys,
        }),
      });
      if (res.ok) {
        const scored = wheelAreas.filter(a => a.score);
        const avgScore = scored.length ? Math.round(scored.reduce((s, a) => s + a.score, 0) / scored.length * 10) / 10 : 0;
        trackWheelSaved({ areasCount: scored.length, focusAreasCount: focusKeys.length, avgScore });
        toast.success("Wheel of Life saved!");
        setHasExisting(true);
        // Update saved state snapshot
        savedStateRef.current = {
          keys: new Set(activeKeys),
          scores: { ...Object.fromEntries(wheelAreas.map(a => [a.key, a.score])) },
          reflections: JSON.parse(JSON.stringify(
            Object.fromEntries(wheelAreas.map(a => [a.key, reflections[a.key] || {}]))
          )),
          focusKeys: [...focusKeys],
        };
        isDirtyRef.current = false;
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--color-brand-primary)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  return (
    <div data-testid="wheel-of-life-page">
      {guest && (
        <SEO
          title="Free Wheel of Life Assessment Tool"
          description="Rate 10 key areas of your life and visualize your balance with this free Wheel of Life tool. Identify where to focus for personal growth."
          path="/wheel"
        />
      )}
      {/* Unsaved changes dialog */}
      {showLeaveDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="unsaved-dialog">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowLeaveDialog(false)} />
          <div className="relative rounded-2xl p-6 max-w-sm mx-4 shadow-xl"
            style={{ background: "var(--color-surface)", border: "1px solid var(--color-border-default)" }}>
            <h3 className="text-base font-bold mb-2" style={{ color: "var(--color-text-primary)" }}>
              Unsaved changes
            </h3>
            <p className="text-sm mb-5" style={{ color: "var(--color-text-secondary)" }}>
              You have unsaved changes to your Wheel of Life. Would you like to save before leaving?
            </p>
            <div className="flex gap-2">
              <Button variant="outline"
                onClick={confirmLeave(false)}
                className="rounded-full h-9 px-4 text-sm flex-1"
                data-testid="discard-btn">
                Discard
              </Button>
              <Button
                onClick={confirmLeave(true)}
                className="rounded-full h-9 px-4 text-sm font-semibold flex-1"
                style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
                data-testid="save-and-leave-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" /> Save & Leave
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Focus selection popup */}
      {showFocusPopup && (
        <FocusPopup
          areas={wheelAreas}
          selectedFocus={focusKeys}
          onConfirm={(keys) => {
            setFocusKeys(keys);
            setShowFocusPopup(false);
            trackFocusAreasSelected(keys);
            toast.success("Focus areas set!");
          }}
          onClose={() => setShowFocusPopup(false)}
          guest={guest}
          onGuestSave={(keys) => {
            setFocusKeys(keys);
            setShowFocusPopup(false);
            trackFocusAreasSelected(keys);
            // Save to localStorage with the selected focus keys, then show auth gate
            const data = {
              scores,
              reflections,
              focusKeys: keys,
              activeKeys: [...activeKeys],
            };
            localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(data));
            setShowAuthGate(true);
          }}
        />
      )}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          {guest ? (
            <button onClick={() => {
              const hasWork = Object.values(scores).some(s => s);
              if (hasWork) {
                setShowAuthGate(true);
              } else {
                navigate("/");
              }
            }}
              className="flex items-center gap-1.5 text-sm font-medium transition-colors"
              style={{ color: "var(--color-text-interactive)" }}
              data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4" /> Home
            </button>
          ) : (
            <button onClick={() => navigate("/myself")}
              className="flex items-center gap-1.5 text-sm font-medium transition-colors"
              style={{ color: "var(--color-text-interactive)" }}
              data-testid="back-to-myself">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
          {guest ? (
            <EvoluraLogo size={32} variant="dark" />
          ) : null}
        </div>

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-1" style={{ color: "var(--color-text-primary)" }}>
            {t("wheel.title")}
          </h1>
          <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
            {t("wheel.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left: Wheel */}
          <div className="rounded-2xl p-6"
            style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}>
            <WheelChart areas={wheelAreas} onScoreChange={handleScoreChange} focusKeys={focusKeys} userPicture={user?.picture} />
            <BalanceMeter areas={wheelAreas} />
          </div>

          {/* Right: Area list with toggles + scores */}
          <div className="rounded-2xl p-5"
            style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}>
            <div className="flex items-center justify-between mb-1 gap-2">
              <h3 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                {t("wheel.lifeAreas")}
              </h3>
              <button
                onClick={() => setShowCustomizeDialog(true)}
                className="flex items-center gap-1 text-[11px] font-medium rounded-full px-2.5 py-1 border border-violet-200 text-violet-700 hover:bg-violet-50 transition-colors"
                data-testid="customize-areas-button"
              >
                <Settings className="w-3 h-3" />
                {t("wheel.customize")}
              </button>
            </div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                {t("wheel.scaleHint")}
              </p>
              <span className="text-xs" style={{ color: scoredCount === wheelAreas.length ? "var(--color-strengths-text)" : "var(--color-text-placeholder)" }}>
                {t("wheel.ratedCount", { done: scoredCount, total: wheelAreas.length })}
              </span>
            </div>

            <div className="divide-y" style={{ borderColor: "var(--color-border-default)" }}>
              {wheelAreas.map(area => (
                  <AreaRow key={area.key} area={{ ...area, score: scores[area.key] || null }}
                    onScoreChange={handleScoreChange}
                    reflection={reflections[area.key]}
                    onReflectionChange={handleReflectionChange}
                    isExpanded={expandedKey === area.key}
                    onFocusArea={setExpandedKey}
                    isFocused={focusKeys.includes(area.key)}
                    linkedItems={linkedItemsMap[area.key] || []}
                    onLinkFromSwot={handleLinkFromSwot}
                    onUnlink={handleUnlinkFromWheel}
                    swotItems={swotData}
                    guest={guest} />
                ))}
            </div>

            {/* Focus areas button — always visible */}
            <button onClick={() => setShowFocusPopup(true)}
              className="flex items-center gap-1.5 mt-3 text-xs font-medium transition-colors"
              style={{ color: "var(--color-text-interactive)" }}
              data-testid="change-focus-btn">
              <Target className="w-3 h-3" /> {focusKeys.length > 0 ? "Change focus areas" : "Select 3 focus areas"}
            </button>

            {/* Actions */}
            <div className="mt-5">
              <Button
                onClick={saveWheel}
                disabled={!canSave || saving}
                className="rounded-full h-10 px-6 text-sm font-semibold w-full"
                style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
                data-testid="save-wheel-btn">
                {saving ? "Saving..." : guest ? (
                  <><Save className="w-3.5 h-3.5 mr-1.5" /> Sign in to save</>
                ) : (
                  <><Save className="w-3.5 h-3.5 mr-1.5" /> Save progress</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Guest auth gate modal */}
      {guest && (
        <GuestAuthGate
          open={showAuthGate}
          onOpenChange={setShowAuthGate}
          title="Sign in to save your Wheel"
          subtitle="Your scores and reflections are safe — they'll be saved automatically after signing in."
        />
      )}

      {/* Customize Areas dialog */}
      <CustomizeAreasDialog
        open={showCustomizeDialog}
        onOpenChange={setShowCustomizeDialog}
        defaultAreas={defaultAreas}
        suggestedAreas={suggestedAreas}
        customAreas={allAreas.filter((a) => a.key.startsWith("custom_"))}
        activeKeys={activeKeys}
        maxAreas={maxAreas}
        onApply={handleCustomizeApply}
      />
    </div>
  );
};

export default WheelOfLifePage;
